import { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  ArrowUpRight,
  Banknote,
  ArrowDownUp,
  Calendar,
  CalendarDays,
  List,
  LayoutGrid,
  User,
  Plus,
  Scale,
} from 'lucide-react';
import { useStore } from '../store/useStore';
import { formatCurrency, formatDate, INVOICE_STATUS_CONFIG, overdueStatusUpdates, invoiceTimelineDate } from '../lib/utils';
import { InvoiceBadge } from '../components/Badge';
import QuickPayModal from '../components/QuickPayModal';
import Modal from '../components/Modal';
import ProfitabilityTable from '../components/ProfitabilityTable';
import type { InvoiceMilestone, InvoiceStatus } from '../types';
import {
  parseISO,
  format,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  startOfYear,
  endOfYear,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  addMonths,
  addQuarters,
  isSameMonth,
  isSameDay,
  isToday,
  isAfter,
  isBefore,
  startOfToday,
} from 'date-fns';

// ─── helpers ────────────────────────────────────────────────────────────────

// Coerce to Number to handle Prisma Decimal fields serialised as strings
function sum(nums: (number | string)[]) {
  return nums.reduce<number>((a, b) => a + Number(b), 0);
}

function pct(part: number, total: number) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}

// ─── SVG bar chart ───────────────────────────────────────────────────────────

interface BarData {
  label: string;
  key: string;
  isPast: boolean;
  paid: number;
  sent: number;
  upcoming: number;
  overdue: number;
}

function MonthlyBarChart({
  bars,
  selectedKey,
  onSelect,
}: {
  bars: BarData[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
}) {
  const maxVal = Math.max(...bars.map((b) => b.paid + b.sent + b.upcoming + b.overdue), 1);
  const H = 160;
  const PAD_TOP = 16; // headroom so the tallest bar's total label stays inside the svg
  const BAR_W = 44;
  const GAP = 20;
  const W = bars.length * (BAR_W + GAP);

  return (
    <div className="overflow-x-auto scrollbar-thin">
      <svg width={Math.max(W, 400)} height={PAD_TOP + H + 40} className="block">
        {[0.25, 0.5, 0.75, 1].map((frac) => {
          const y = PAD_TOP + H - H * frac;
          return (
            <g key={frac}>
              <line x1={0} y1={y} x2={W} y2={y} stroke="#f3f4f6" strokeWidth={1} />
              <text x={2} y={y - 3} fontSize={9} fill="#d1d5db">
                {formatCurrency(maxVal * frac)}
              </text>
            </g>
          );
        })}
        {bars.map((bar, i) => {
          const x = i * (BAR_W + GAP);
          const total = bar.paid + bar.sent + bar.upcoming + bar.overdue;
          const isSelected = bar.key === selectedKey;
          const segments = [
            { val: bar.paid, color: '#22c55e' },
            { val: bar.sent, color: '#3b82f6' },
            { val: bar.upcoming, color: '#e5e7eb' },
            { val: bar.overdue, color: '#ef4444' },
          ];
          let yOffset = PAD_TOP + H;
          return (
            <g key={bar.key} className="cursor-pointer" onClick={() => onSelect(bar.key)}>
              {/* Selection highlight / click target spanning full height */}
              <rect
                x={x - GAP / 2}
                y={0}
                width={BAR_W + GAP}
                height={PAD_TOP + H + 32}
                fill={isSelected ? '#eef2ff' : 'transparent'}
                rx={6}
              />
              {segments.map(({ val, color }, si) => {
                if (!val) return null;
                const barH = (val / maxVal) * H;
                yOffset -= barH;
                return (
                  <rect
                    key={si}
                    x={x}
                    y={yOffset}
                    width={BAR_W}
                    height={barH}
                    fill={color}
                    rx={si === segments.filter((s) => s.val > 0).length - 1 ? 4 : 0}
                    opacity={isSelected ? 1 : 0.85}
                  />
                );
              })}
              {total > 0 && (
                <text x={x + BAR_W / 2} y={yOffset - 4} textAnchor="middle" fontSize={9} fontWeight={600} fill="#6b7280">
                  {formatCurrency(total)}
                </text>
              )}
              <text x={x + BAR_W / 2} y={PAD_TOP + H + 16} textAnchor="middle" fontSize={10} fontWeight={isSelected ? 700 : 400} fill={isSelected ? '#4f46e5' : '#9ca3af'}>
                {bar.label.split(' ')[0]}
              </text>
              <text x={x + BAR_W / 2} y={PAD_TOP + H + 28} textAnchor="middle" fontSize={9} fill={isSelected ? '#818cf8' : '#d1d5db'}>
                {bar.label.split(' ')[1]}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ─── Inline invoice status dropdown ──────────────────────────────────────────

const STATUS_ORDER: InvoiceStatus[] = ['not_ready', 'ready_to_send', 'sent', 'paid', 'overdue'];

function StatusSelect({
  inv,
  onChange,
}: {
  inv: InvoiceMilestone;
  onChange: (updates: Partial<InvoiceMilestone>) => void;
}) {
  const cfg = INVOICE_STATUS_CONFIG[inv.status];
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    const status = e.target.value as InvoiceStatus;
    if (status === inv.status) return;
    if (status === 'overdue') {
      // Manual overdue must also carry a past due date, or the server's
      // auto-rule flips it straight back to 'sent'.
      onChange(overdueStatusUpdates(inv));
      return;
    }
    const updates: Partial<InvoiceMilestone> = { status };
    // Keep the dated columns coherent so cash-flow views stay correct.
    const today = new Date().toISOString().split('T')[0];
    if (status === 'paid' && !inv.paidDate) updates.paidDate = today;
    if (status === 'sent' && !inv.sentDate) updates.sentDate = today;
    onChange(updates);
  };
  return (
    <select
      value={inv.status}
      onClick={(e) => e.stopPropagation()}
      onChange={handleChange}
      className={`text-xs font-medium rounded-full pl-2.5 pr-6 py-1 border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-400 appearance-none bg-[length:14px] bg-[right_0.35rem_center] bg-no-repeat ${cfg.bg} ${cfg.color}`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E\")",
      }}
      title="Muuda staatust"
    >
      {STATUS_ORDER.map((s) => (
        <option key={s} value={s} className="bg-white text-gray-700">
          {INVOICE_STATUS_CONFIG[s].label}
        </option>
      ))}
    </select>
  );
}

// ─── Quick add invoice (from calendar) ───────────────────────────────────────

function QuickAddInvoiceModal({
  open,
  date,
  projects,
  onClose,
  onSave,
}: {
  open: boolean;
  date: string;
  projects: { id: string; name: string; clientName: string }[];
  onClose: () => void;
  onSave: (inv: Omit<InvoiceMilestone, 'id'>) => void;
}) {
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('Arve');
  const [amount, setAmount] = useState<number>(0);
  const [vatRate, setVatRate] = useState<number>(24);
  const [sendDate, setSendDate] = useState(date);
  const [status, setStatus] = useState<InvoiceStatus>('not_ready');
  const [saving, setSaving] = useState(false);

  // Reset the form each time the modal is opened for a (new) date.
  useEffect(() => {
    if (open) {
      setSendDate(date);
      setProjectId('');
      setTitle('Arve');
      setAmount(0);
      setVatRate(24);
      setStatus('not_ready');
      setSaving(false);
    }
  }, [open, date]);

  const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500';
  const canSave = !!projectId && title.trim().length > 0 && amount > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    await onSave({
      projectId,
      relatedStageId: null,
      title: title.trim(),
      amount,
      vatRate,
      percentage: 0,
      plannedSendDate: sendDate,
      sentDate: '',
      paidDate: '',
      status,
      notes: '',
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Lisa arve" size="md">
      <div className="space-y-4">
        <p className="text-xs text-gray-400">
          Uus arve kuupäevaga{' '}
          <span className="font-medium text-gray-600">{sendDate ? formatDate(sendDate) : '—'}</span>
        </p>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Projekt *</label>
          <select autoFocus value={projectId} onChange={(e) => setProjectId(e.target.value)} className={inputCls}>
            <option value="">— Vali projekt —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.clientName ? ` · ${p.clientName}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Arve nimi *</label>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} placeholder="nt Makse 1/4" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Summa (€) *</label>
            <input type="number" value={amount || ''} onChange={(e) => setAmount(Number(e.target.value))} className={inputCls} placeholder="0" />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">KM %</label>
            <input type="number" min={0} max={100} value={vatRate} onChange={(e) => setVatRate(Number(e.target.value))} className={inputCls} />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Tähtaeg</label>
            <input type="date" value={sendDate} onChange={(e) => setSendDate(e.target.value)} className={inputCls} />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 block mb-1">Staatus</label>
          <select value={status} onChange={(e) => setStatus(e.target.value as InvoiceStatus)} className={inputCls}>
            {STATUS_ORDER.map((s) => (
              <option key={s} value={s}>{INVOICE_STATUS_CONFIG[s].label}</option>
            ))}
          </select>
        </div>
        {amount > 0 && (
          <p className="text-xs text-gray-400">
            {formatCurrency(amount)} + {vatRate}% KM = <span className="font-medium text-gray-600">{formatCurrency(amount * (1 + vatRate / 100))}</span>
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Tühista</button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-brand-500 text-white rounded-lg hover:bg-brand-600 font-medium disabled:opacity-50"
          >
            <Plus size={14} /> {saving ? 'Salvestan…' : 'Lisa arve'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── Calendar view of upcoming invoices ──────────────────────────────────────

const STATUS_COLOR: Record<string, string> = {
  paid: '#22c55e',
  sent: '#3b82f6',
  overdue: '#ef4444',
  ready_to_send: '#eab308',
  not_ready: '#d1d5db',
};

function InvoiceCalendar({
  invoices,
  clientName,
  onSelect,
  onAddForDate,
}: {
  invoices: InvoiceMilestone[];
  clientName: (projectId: string) => string;
  onSelect: (projectId: string) => void;
  onAddForDate: (dateStr: string) => void;
}) {
  const [month, setMonth] = useState(startOfMonth(new Date()));

  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(month), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
    return eachDayOfInterval({ start: gridStart, end: gridEnd });
  }, [month]);

  // Map yyyy-MM-dd → invoices on that day (sent date once sent, else planned)
  const byDay = useMemo(() => {
    const m = new Map<string, InvoiceMilestone[]>();
    for (const inv of invoices) {
      const basis = invoiceTimelineDate(inv);
      if (!basis) continue;
      const key = basis.split('T')[0];
      if (!m.has(key)) m.set(key, []);
      m.get(key)!.push(inv);
    }
    return m;
  }, [invoices]);

  const monthTotal = useMemo(
    () =>
      invoices
        .filter((i) => { const b = invoiceTimelineDate(i); return !!b && isSameMonth(parseISO(b), month); })
        .reduce((s, i) => s + Number(i.amount), 0),
    [invoices, month],
  );

  const weekdays = ['E', 'T', 'K', 'N', 'R', 'L', 'P'];

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-50 flex-wrap gap-2">
        <div className="flex items-center gap-1.5">
          <button onClick={() => setMonth((m) => addMonths(m, -1))} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><ChevronLeft size={16} /></button>
          <span className="text-sm font-semibold text-gray-800 capitalize w-36 text-center">{format(month, 'MMMM yyyy')}</span>
          <button onClick={() => setMonth((m) => addMonths(m, 1))} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-500"><ChevronRight size={16} /></button>
          <button onClick={() => setMonth(startOfMonth(new Date()))} className="ml-1 text-xs px-2 py-1 border border-gray-200 rounded-lg text-gray-500 hover:bg-gray-50">Täna</button>
        </div>
        <span className="text-xs text-gray-400">Kuu kokku: <span className="font-bold text-gray-700">{formatCurrency(monthTotal)}</span></span>
      </div>

      {/* Weekday header */}
      <div className="grid grid-cols-7 border-b border-gray-50">
        {weekdays.map((d) => (
          <div key={d} className="px-2 py-2 text-center text-xs font-medium text-gray-400">{d}</div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const key = format(day, 'yyyy-MM-dd');
          const dayInvoices = byDay.get(key) ?? [];
          const inMonth = isSameMonth(day, month);
          return (
            <div
              key={key}
              onClick={() => onAddForDate(key)}
              className={`group relative min-h-[96px] border-b border-r border-gray-50 p-1.5 cursor-pointer hover:bg-brand-50/40 transition-colors ${inMonth ? '' : 'bg-gray-50/40'}`}
              title="Lisa arve sellele kuupäevale"
            >
              <div className="flex items-center justify-between mb-1">
                <div className={`text-xs ${
                  isToday(day)
                    ? 'w-5 h-5 rounded-full bg-brand-500 text-white flex items-center justify-center font-semibold'
                    : inMonth ? 'text-gray-500' : 'text-gray-300'
                }`}>
                  {format(day, 'd')}
                </div>
                <Plus size={13} className="text-brand-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="space-y-1">
                {dayInvoices.slice(0, 3).map((inv) => (
                  <button
                    key={inv.id}
                    onClick={(e) => { e.stopPropagation(); onSelect(inv.projectId); }}
                    className="w-full px-1.5 py-0.5 rounded text-left hover:bg-gray-100 bg-gray-50 border-l-2 block"
                    style={{ borderColor: STATUS_COLOR[inv.status] ?? '#d1d5db' }}
                    title={`${clientName(inv.projectId)} · ${inv.title} · ${formatCurrency(Number(inv.amount))}`}
                  >
                    <span className="text-[10px] text-gray-700 truncate block leading-tight">{clientName(inv.projectId)}</span>
                    <span className="text-[9px] font-medium text-gray-400 truncate block leading-tight">{formatCurrency(Number(inv.amount))}</span>
                  </button>
                ))}
                {dayInvoices.length > 3 && (
                  <span className="text-[10px] text-gray-400 pl-1.5 block">+{dayInvoices.length - 3} veel</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

type Tab = 'overview' | 'invoices' | 'profit';

export default function Finance() {
  const allProjects = useStore((s) => s.projects);
  const clients = useStore((s) => s.clients);
  const allInvoices = useStore((s) => s.invoices);
  const users = useStore((s) => s.users);
  const addInvoice = useStore((s) => s.addInvoice);
  const updateInvoice = useStore((s) => s.updateInvoice);
  const navigate = useNavigate();

  const today = startOfToday();

  // ── Quick pay modal ───────────────────────────────────────────────────────
  type QuickPayTarget = { projectId: string; projectName: string; projectValue: number; paidSoFar: number };
  const [quickPayTarget, setQuickPayTarget] = useState<QuickPayTarget | null>(null);

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const [tab, setTab] = useState<Tab>('overview');

  // ── Filters ───────────────────────────────────────────────────────────────
  const [selectedSalesId, setSelectedSalesId] = useState<string>('all');
  const [selectedClientId, setSelectedClientId] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [overviewSort, setOverviewSort] = useState<string>('value_desc');

  // Unique salespeople who own at least one project
  const salespeople = useMemo(() => {
    const ids = new Set(allProjects.map((p) => p.salesOwnerId));
    return users.filter((u) => ids.has(u.id)).sort((a, b) => a.name.localeCompare(b.name));
  }, [allProjects, users]);

  // Unique clients who have at least one project
  const projectClients = useMemo(() => {
    const ids = new Set(allProjects.map((p) => p.clientId));
    return clients.filter((c) => ids.has(c.id)).sort((a, b) => a.companyName.localeCompare(b.companyName));
  }, [allProjects, clients]);

  // ── Filtered data ─────────────────────────────────────────────────────────
  const projects = useMemo(() => {
    return allProjects.filter((p) => {
      if (selectedSalesId !== 'all' && p.salesOwnerId !== selectedSalesId) return false;
      if (selectedClientId !== 'all' && p.clientId !== selectedClientId) return false;
      if (selectedStatus !== 'all' && p.status !== selectedStatus) return false;
      return true;
    });
  }, [allProjects, selectedSalesId, selectedClientId, selectedStatus]);

  const projectIds = useMemo(() => new Set(projects.map((p) => p.id)), [projects]);

  const invoices = useMemo(
    () => allInvoices.filter((i) => projectIds.has(i.projectId)),
    [allInvoices, projectIds],
  );

  // ── Pipeline filters (Invoices tab) ───────────────────────────────────────
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [pipelineView, setPipelineView] = useState<'list' | 'calendar'>('list');
  const [quickAddDate, setQuickAddDate] = useState<string | null>(null);
  const [tableSort, setTableSort] = useState<{ col: string; dir: 'asc' | 'desc' }>({ col: 'plannedSendDate', dir: 'asc' });

  // All projects with client names, for the calendar quick-add project picker.
  const projectOptions = useMemo(
    () =>
      allProjects
        .map((p) => ({ id: p.id, name: p.name, clientName: clients.find((c) => c.id === p.clientId)?.companyName ?? '' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allProjects, clients],
  );

  // Client name for a project — used by the calendar view
  const clientNameForProject = useCallback(
    (projectId: string) => {
      const project = allProjects.find((p) => p.id === projectId);
      return clients.find((c) => c.id === project?.clientId)?.companyName ?? '—';
    },
    [allProjects, clients],
  );

  // Invoices shown in the calendar: project-filtered, respecting the status filter,
  // but the calendar navigates months on its own so date-range filters don't apply.
  const calendarInvoices = useMemo(
    () => invoices.filter((i) => statusFilter === 'all' || i.status === statusFilter),
    [invoices, statusFilter],
  );

  // ── Aggregates ────────────────────────────────────────────────────────────
  const totalProjectValue = sum(projects.map((p) => p.value));

  const paidInvoices    = invoices.filter((i) => i.status === 'paid');
  const sentInvoices    = invoices.filter((i) => i.status === 'sent');
  const overdueInvoices = invoices.filter((i) => i.status === 'overdue');
  const upcomingInvoices = invoices.filter((i) => i.status === 'not_ready' || i.status === 'ready_to_send');

  const totalPaid     = sum(paidInvoices.map((i) => i.amount));
  const totalSent     = sum(sentInvoices.map((i) => i.amount));
  const totalOverdue  = sum(overdueInvoices.map((i) => i.amount));
  const totalUpcoming = sum(upcomingInvoices.map((i) => i.amount));

  // ── Monthly bars ──────────────────────────────────────────────────────────
  // How many months of history to show in the chart (forward window is fixed at 8).
  const [monthsBack, setMonthsBack] = useState(6);
  const MONTHS_FORWARD = 8;
  const [selectedMonthKey, setSelectedMonthKey] = useState<string>(format(today, 'yyyy-MM'));

  const bars: BarData[] = useMemo(() => {
    const monthStart = startOfMonth(addMonths(today, -monthsBack));
    return Array.from({ length: monthsBack + MONTHS_FORWARD }, (_, i) => {
      const month = addMonths(monthStart, i);
      const label = format(month, 'MMM yyyy');
      const key = format(month, 'yyyy-MM');
      const inMonth = (dateStr: string) => dateStr ? isSameMonth(parseISO(dateStr), month) : false;

      // Each invoice sits in the month its money moved: paid date, else the date
      // it actually went out, else the planned date — never vanishing because a
      // date was missing.
      const inIts = (inv: (typeof invoices)[number]) => inMonth(invoiceTimelineDate(inv) ?? '');
      const paid = sum(invoices.filter((inv) => inv.status === 'paid' && inIts(inv)).map((i) => i.amount));
      const sent = sum(invoices.filter((inv) => inv.status === 'sent' && inIts(inv)).map((i) => i.amount));
      const overdue = sum(invoices.filter((inv) => inv.status === 'overdue' && inIts(inv)).map((i) => i.amount));
      const upcoming = sum(invoices.filter((inv) => (inv.status === 'not_ready' || inv.status === 'ready_to_send') && inIts(inv)).map((i) => i.amount));

      return { label, key, isPast: isBefore(month, startOfMonth(today)), paid, sent, upcoming, overdue };
    });
  }, [invoices, monthsBack, today]);

  // ── Per-client breakdown for the selected month ───────────────────────────
  // An invoice belongs to the month its money moved: paid date, else the date
  // it was actually sent, else the planned date (see invoiceTimelineDate).
  const monthClientBreakdown = useMemo(() => {
    const monthDate = parseISO(`${selectedMonthKey}-01`);
    const invoiceMonthKey = (inv: (typeof invoices)[number]) => {
      const basis = invoiceTimelineDate(inv);
      return basis ? format(parseISO(basis), 'yyyy-MM') : null;
    };

    const monthInvoices = invoices.filter((inv) => invoiceMonthKey(inv) === selectedMonthKey);

    type ClientGroup = {
      clientId: string;
      clientName: string;
      items: { inv: (typeof invoices)[number]; projectName: string }[];
      paid: number; sent: number; overdue: number; upcoming: number; total: number;
    };
    const groups = new Map<string, ClientGroup>();

    for (const inv of monthInvoices) {
      const project = projects.find((p) => p.id === inv.projectId);
      const client = project ? clients.find((c) => c.id === project.clientId) : undefined;
      const clientId = client?.id ?? 'unknown';
      if (!groups.has(clientId)) {
        groups.set(clientId, {
          clientId,
          clientName: client?.companyName ?? 'Tundmatu klient',
          items: [], paid: 0, sent: 0, overdue: 0, upcoming: 0, total: 0,
        });
      }
      const g = groups.get(clientId)!;
      g.items.push({ inv, projectName: project?.name ?? '—' });
      const amt = Number(inv.amount);
      g.total += amt;
      if (inv.status === 'paid') g.paid += amt;
      else if (inv.status === 'sent') g.sent += amt;
      else if (inv.status === 'overdue') g.overdue += amt;
      else g.upcoming += amt;
    }

    const list = Array.from(groups.values()).sort((a, b) => b.total - a.total);
    const totals = {
      paid: sum(monthInvoices.filter((i) => i.status === 'paid').map((i) => i.amount)),
      sent: sum(monthInvoices.filter((i) => i.status === 'sent').map((i) => i.amount)),
      overdue: sum(monthInvoices.filter((i) => i.status === 'overdue').map((i) => i.amount)),
      upcoming: sum(monthInvoices.filter((i) => i.status === 'not_ready' || i.status === 'ready_to_send').map((i) => i.amount)),
      all: sum(monthInvoices.map((i) => i.amount)),
      count: monthInvoices.length,
    };
    return { monthDate, label: format(monthDate, 'MMMM yyyy'), list, totals };
  }, [selectedMonthKey, invoices, projects, clients]);

  // ── Future invoices (calendar, invoices tab) ──────────────────────────────
  type MonthGroup = { monthLabel: string; monthDate: Date; items: typeof invoices; total: number };

  const futureInvoices = [...invoices]
    .filter((i) => {
      const basis = invoiceTimelineDate(i);
      if (!basis) return false;
      const nonPaid = i.status !== 'paid';
      if (!nonPaid) return false;
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      const d = parseISO(basis);
      if (dateFrom && isBefore(d, parseISO(dateFrom))) return false;
      if (dateTo && isAfter(d, parseISO(dateTo))) return false;
      return true;
    })
    .sort((a, b) => {
      const diff = parseISO(invoiceTimelineDate(a) as string).getTime() - parseISO(invoiceTimelineDate(b) as string).getTime();
      return sortOrder === 'asc' ? diff : -diff;
    });

  const monthGroups = new Map<string, MonthGroup>();
  futureInvoices.forEach((inv) => {
    const basis = invoiceTimelineDate(inv) as string;
    const key = format(parseISO(basis), 'yyyy-MM');
    if (!monthGroups.has(key)) {
      monthGroups.set(key, {
        monthLabel: format(parseISO(basis), 'MMMM yyyy'),
        monthDate: parseISO(basis),
        items: [],
        total: 0,
      });
    }
    const g = monthGroups.get(key)!;
    g.items.push(inv);
    g.total += Number(inv.amount);
  });

  const sortedMonthGroups = Array.from(monthGroups.values()).sort(
    (a, b) => a.monthDate.getTime() - b.monthDate.getTime(),
  );

  // ── Per-project breakdown ─────────────────────────────────────────────────
  const projectRows = useMemo(() => {
    const rows = projects
      .filter((p) => Number(p.value) > 0)
      .map((p) => {
        const client = clients.find((c) => c.id === p.clientId);
        const salesOwner = users.find((u) => u.id === p.salesOwnerId);
        const projInvoices = invoices.filter((i) => i.projectId === p.id);
        const paid    = sum(projInvoices.filter((i) => i.status === 'paid').map((i) => i.amount));
        const sent    = sum(projInvoices.filter((i) => i.status === 'sent').map((i) => i.amount));
        const overdue = sum(projInvoices.filter((i) => i.status === 'overdue').map((i) => i.amount));
        const remaining = Number(p.value) - paid - sent - overdue;
        const hasOverdue = projInvoices.some((i) => i.status === 'overdue');
        const nextInvoice = projInvoices
          .filter((i) => i.status !== 'paid' && i.plannedSendDate)
          .sort((a, b) => a.plannedSendDate.localeCompare(b.plannedSendDate))[0];
        return { project: p, client, salesOwner, paid, sent, overdue, remaining, hasOverdue, nextInvoice };
      });

    return rows.sort((a, b) => {
      switch (overviewSort) {
        case 'value_desc': return Number(b.project.value) - Number(a.project.value);
        case 'value_asc': return Number(a.project.value) - Number(b.project.value);
        case 'start_asc': return a.project.startDate.localeCompare(b.project.startDate);
        case 'start_desc': return b.project.startDate.localeCompare(a.project.startDate);
        case 'end_asc': return a.project.endDate.localeCompare(b.project.endDate);
        case 'end_desc': return b.project.endDate.localeCompare(a.project.endDate);
        case 'next_invoice': {
          const da = a.nextInvoice?.plannedSendDate ?? '9999';
          const db = b.nextInvoice?.plannedSendDate ?? '9999';
          return da.localeCompare(db);
        }
        case 'overdue_first': return Number(b.overdue) - Number(a.overdue);
        default: return 0;
      }
    });
  }, [projects, invoices, clients, users, overviewSort]);

  // ── Invoice detail table ──────────────────────────────────────────────────
  const invoiceRows = useMemo(() => {
    const rows = invoices.map((inv) => {
      const project = projects.find((p) => p.id === inv.projectId);
      const client = project ? clients.find((c) => c.id === project.clientId) : undefined;
      const salesOwner = project ? users.find((u) => u.id === project.salesOwnerId) : undefined;
      return { inv, project, client, salesOwner };
    });

    const dir = tableSort.dir === 'asc' ? 1 : -1;
    return rows.sort((a, b) => {
      switch (tableSort.col) {
        case 'plannedSendDate': {
          const da = invoiceTimelineDate(a.inv) ?? '';
          const db = invoiceTimelineDate(b.inv) ?? '';
          return da < db ? -dir : da > db ? dir : 0;
        }
        case 'amount':
          return (Number(a.inv.amount) - Number(b.inv.amount)) * dir;
        case 'status':
          return a.inv.status.localeCompare(b.inv.status) * dir;
        case 'project':
          return (a.project?.name ?? '').localeCompare(b.project?.name ?? '') * dir;
        case 'client':
          return (a.client?.companyName ?? '').localeCompare(b.client?.companyName ?? '') * dir;
        default:
          return 0;
      }
    });
  }, [invoices, projects, clients, users, tableSort]);

  function toggleSort(col: string) {
    setTableSort((prev) => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  }

  function SortIcon({ col }: { col: string }) {
    if (tableSort.col !== col) return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1 text-brand-500">{tableSort.dir === 'asc' ? '↑' : '↓'}</span>;
  }

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Finantside ülevaade</h1>
          <p className="text-gray-500 text-sm mt-1">
            {projects.filter((p) => Number(p.value) > 0).length} projekti ·{' '}
            {formatDate(today.toISOString())}
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <User size={14} className="text-gray-400 shrink-0" />
          <select
            value={selectedSalesId}
            onChange={(e) => setSelectedSalesId(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 bg-white"
          >
            <option value="all">Kõik müügijuhid</option>
            {salespeople.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>
          <select
            value={selectedClientId}
            onChange={(e) => setSelectedClientId(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 bg-white"
          >
            <option value="all">Kõik kliendid</option>
            {projectClients.map((c) => (
              <option key={c.id} value={c.id}>{c.companyName}</option>
            ))}
          </select>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-400 text-gray-700 bg-white"
          >
            <option value="all">Kõik staatused</option>
            <option value="active">Active</option>
            <option value="draft">Draft</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          {(selectedSalesId !== 'all' || selectedClientId !== 'all' || selectedStatus !== 'all') && (
            <button
              onClick={() => { setSelectedSalesId('all'); setSelectedClientId('all'); setSelectedStatus('all'); }}
              className="text-xs text-brand-500 hover:text-brand-700 font-medium"
            >
              Tühista
            </button>
          )}
        </div>
      </div>

      {/* Tab selector */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setTab('overview')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'overview'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <LayoutGrid size={14} /> Ülevaade
        </button>
        <button
          onClick={() => setTab('invoices')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'invoices'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <List size={14} /> Arved ({invoices.length})
        </button>
        <button
          onClick={() => setTab('profit')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
            tab === 'profit'
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Scale size={14} /> Tasuvus
        </button>
      </div>

      {/* ═══════════════════════════════ TAB: OVERVIEW ══════════════════════ */}
      {tab === 'overview' && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Projektide koguväärtus</p>
                <div className="p-2 bg-gray-100 rounded-lg"><Banknote size={15} className="text-gray-500" /></div>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalProjectValue)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {projects.filter((p) => p.status === 'active').length} aktiivset projekti
              </p>
            </div>

            <div className="bg-white rounded-xl border border-green-100 p-5">
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Laekunud</p>
                <div className="p-2 bg-green-100 rounded-lg"><CheckCircle2 size={15} className="text-green-600" /></div>
              </div>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</p>
              <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-green-400 rounded-full transition-all" style={{ width: `${pct(totalPaid, totalProjectValue)}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1">{pct(totalPaid, totalProjectValue)}% koguväärtusest</p>
            </div>

            <div className="bg-white rounded-xl border border-blue-100 p-5">
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Saadetud, ootel</p>
                <div className="p-2 bg-blue-100 rounded-lg"><Clock size={15} className="text-blue-600" /></div>
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalSent)}</p>
              <p className="text-xs text-gray-400 mt-1">{sentInvoices.length} arvet kliendi juures</p>
            </div>

            <div className={`bg-white rounded-xl border p-5 ${totalOverdue > 0 ? 'border-red-100' : 'border-gray-100'}`}>
              <div className="flex items-start justify-between mb-3">
                <p className="text-xs font-medium text-gray-400">Planeeritud arved</p>
                <div className={`p-2 rounded-lg ${totalOverdue > 0 ? 'bg-red-100' : 'bg-yellow-100'}`}>
                  {totalOverdue > 0
                    ? <AlertTriangle size={15} className="text-red-500" />
                    : <TrendingUp size={15} className="text-yellow-600" />}
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">{formatCurrency(totalUpcoming)}</p>
              <p className="text-xs text-gray-400 mt-1">
                {upcomingInvoices.length} planeeritud arvet (veel saatmata)
              </p>
              {totalOverdue > 0 && (
                <div className="mt-3 pt-3 border-t border-red-100 flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-red-500">Tähtaja ületanud</span>
                  <span className="text-sm font-bold text-red-600">
                    {formatCurrency(totalOverdue)}
                    <span className="font-medium text-red-400 ml-1">· {overdueInvoices.length} {overdueInvoices.length === 1 ? 'arve' : 'arvet'}</span>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Monthly chart */}
          <div className="bg-white rounded-xl border border-gray-100 p-6 mb-6">
            <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
              <div>
                <h2 className="font-semibold text-gray-900">Igakuine rahavoog</h2>
                <p className="text-xs text-gray-400 mt-0.5">Eelmised {monthsBack} + järgmised {MONTHS_FORWARD} kuud · vajuta kuule, et näha kliente</p>
              </div>
              <div className="flex gap-4 flex-wrap items-center">
                {/* History range */}
                <div className="flex items-center gap-1">
                  <span className="text-xs text-gray-400 mr-1">Ajalugu:</span>
                  {[2, 6, 12, 24].map((n) => (
                    <button
                      key={n}
                      onClick={() => setMonthsBack(n)}
                      className={`text-xs px-2 py-1 rounded-lg border transition-colors ${
                        monthsBack === n
                          ? 'border-brand-300 bg-brand-50 text-brand-600 font-medium'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {n} kuud
                    </button>
                  ))}
                </div>
                <div className="flex gap-4 flex-wrap">
                  {[
                    { color: 'bg-green-400', label: 'Laekunud' },
                    { color: 'bg-blue-400', label: 'Ootel' },
                    { color: 'bg-gray-200', label: 'Planeeritud' },
                    { color: 'bg-red-400', label: 'Tähtaja ületanud' },
                  ].map(({ color, label }) => (
                    <div key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <div className={`w-3 h-3 rounded-sm ${color}`} />
                      {label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <MonthlyBarChart bars={bars} selectedKey={selectedMonthKey} onSelect={setSelectedMonthKey} />

            {/* Per-client breakdown for the selected month */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-sm font-semibold text-gray-800 capitalize">
                  {monthClientBreakdown.label}
                  <span className="ml-2 text-xs font-normal text-gray-400">
                    {monthClientBreakdown.totals.count} arvet · {monthClientBreakdown.list.length} klienti
                  </span>
                </h3>
                <div className="flex items-center gap-3 flex-wrap text-xs">
                  {monthClientBreakdown.totals.paid > 0 && <span className="text-green-600 font-medium">✓ {formatCurrency(monthClientBreakdown.totals.paid)} laekunud</span>}
                  {monthClientBreakdown.totals.sent > 0 && <span className="text-blue-600">⏳ {formatCurrency(monthClientBreakdown.totals.sent)} ootel</span>}
                  {monthClientBreakdown.totals.overdue > 0 && <span className="text-red-500 font-medium">⚠ {formatCurrency(monthClientBreakdown.totals.overdue)} tähtaja ületanud</span>}
                  {monthClientBreakdown.totals.upcoming > 0 && <span className="text-gray-400">◷ {formatCurrency(monthClientBreakdown.totals.upcoming)} planeeritud</span>}
                </div>
              </div>

              {monthClientBreakdown.list.length === 0 ? (
                <p className="text-sm text-gray-300 py-4 text-center">Selles kuus arveid pole</p>
              ) : (
                <div className="space-y-2">
                  {monthClientBreakdown.list.map((group) => (
                    <div key={group.clientId} className="border border-gray-100 rounded-lg overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-2.5 bg-gray-50">
                        <span className="text-sm font-medium text-gray-800">{group.clientName}</span>
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(group.total)}</span>
                      </div>
                      <div className="divide-y divide-gray-50">
                        {group.items.map(({ inv, projectName }) => (
                          <button
                            key={inv.id}
                            onClick={() => navigate(`/projects/${inv.projectId}/invoices`)}
                            className="w-full flex items-center justify-between px-4 py-2 hover:bg-gray-50 transition-colors text-left"
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-medium text-gray-700 truncate">{inv.title}</p>
                              <p className="text-xs text-gray-400 truncate">{projectName}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-3">
                              <InvoiceBadge status={inv.status} />
                              <span className="text-xs font-semibold text-gray-800 w-20 text-right">{formatCurrency(Number(inv.amount))}</span>
                              <ChevronRight size={13} className="text-gray-300" />
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Per-project breakdown */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-50 flex items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold text-gray-900 text-sm">Projektide finantsülevaade</h2>
                <p className="text-xs text-gray-400 mt-0.5">Laekunud vs. ootel vs. jääk</p>
              </div>
              <select
                value={overviewSort}
                onChange={(e) => setOverviewSort(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-400 text-gray-600 bg-white"
              >
                <option value="value_desc">Väärtus ↓</option>
                <option value="value_asc">Väärtus ↑</option>
                <option value="start_asc">Alguskuupäev ↑</option>
                <option value="start_desc">Alguskuupäev ↓</option>
                <option value="end_asc">Lõppkuupäev ↑</option>
                <option value="end_desc">Lõppkuupäev ↓</option>
                <option value="next_invoice">Järgmine arve</option>
                <option value="overdue_first">Tähtaja ületanud ees</option>
              </select>
            </div>
            <div className="divide-y divide-gray-50 max-h-[480px] overflow-y-auto scrollbar-thin">
              {projectRows.map(({ project, client, salesOwner, paid, sent, overdue, remaining, hasOverdue }) => {
                const paidPct   = pct(paid,    Number(project.value));
                const sentPct   = pct(sent,    Number(project.value));
                const overduePct = pct(overdue, Number(project.value));
                return (
                  <div key={project.id} className="group">
                    <div
                      onClick={() => navigate(`/projects/${project.id}/invoices`)}
                      className="w-full px-5 py-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-gray-900 truncate">{project.name}</span>
                            {hasOverdue && (
                              <span className="shrink-0 text-xs text-red-500 font-medium bg-red-50 px-1.5 py-0.5 rounded">
                                Tähtaja ületanud
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-400">
                            {client?.companyName}
                            {salesOwner && selectedSalesId === 'all' && (
                              <span className="ml-2 text-gray-300">· {salesOwner.name}</span>
                            )}
                          </p>
                        </div>
                        <div className="flex items-start gap-3 shrink-0">
                          <div className="text-right">
                            <p className="text-sm font-bold text-gray-900">{formatCurrency(Number(project.value))}</p>
                            <p className="text-xs text-gray-400">
                              {paid > 0 ? <span className="text-green-600">{formatCurrency(paid)} laekunud</span> : 'koguväärtus'}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setQuickPayTarget({ projectId: project.id, projectName: project.name, projectValue: Number(project.value), paidSoFar: paid });
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 px-2.5 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium whitespace-nowrap"
                            title="Record a payment"
                          >
                            <Banknote size={12} /> Makse
                          </button>
                        </div>
                      </div>

                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden flex">
                        <div className="bg-green-400 h-full" style={{ width: `${paidPct}%` }} />
                        <div className="bg-blue-400 h-full"  style={{ width: `${sentPct}%` }} />
                        <div className="bg-red-400 h-full"   style={{ width: `${overduePct}%` }} />
                      </div>

                      <div className="flex gap-4 mt-2 flex-wrap">
                        {paid > 0    && <span className="text-xs text-green-600 font-medium">✓ {formatCurrency(paid)} laekunud</span>}
                        {sent > 0    && <span className="text-xs text-blue-600">⏳ {formatCurrency(sent)} ootel</span>}
                        {overdue > 0 && <span className="text-xs text-red-500 font-medium">⚠ {formatCurrency(overdue)} tähtaja ületanud</span>}
                        {remaining > 0 && <span className="text-xs text-gray-400">+ {formatCurrency(remaining)} planeerimata</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>Kokku laekunud</span>
                <span className="font-bold text-green-600">{formatCurrency(totalPaid)}</span>
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>Saadetud, ootel maksmist</span>
                <span className="font-bold text-blue-600">{formatCurrency(totalSent)}</span>
              </div>
              {totalOverdue > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                  <span>Tähtaja ületanud</span>
                  <span className="font-bold text-red-500">{formatCurrency(totalOverdue)}</span>
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                <span>Tulevased (planeeritud)</span>
                <span className="font-bold text-gray-700">{formatCurrency(totalUpcoming)}</span>
              </div>
              <div className="flex items-center justify-between text-sm font-semibold border-t border-gray-200 pt-3">
                <span className="text-gray-700">Kõik projektid kokku</span>
                <span className="text-gray-900">{formatCurrency(totalProjectValue)}</span>
              </div>
            </div>
          </div>

          {/* Action items */}
          {(overdueInvoices.length > 0 || invoices.filter((i) => i.status === 'ready_to_send').length > 0) && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 mt-6">
              <h3 className="font-semibold text-amber-800 text-sm mb-3 flex items-center gap-2">
                <AlertTriangle size={15} className="text-amber-500" />
                Vajavad tegevust kohe
              </h3>
              <div className="space-y-2">
                {overdueInvoices.map((inv) => {
                  const project = projects.find((p) => p.id === inv.projectId);
                  const client = clients.find((c) => c.id === project?.clientId);
                  return (
                    <button
                      key={inv.id}
                      onClick={() => navigate(`/projects/${inv.projectId}/invoices`)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-red-100 rounded-lg hover:bg-red-50 text-left transition-colors"
                    >
                      <div>
                        <span className="text-xs font-medium text-gray-800">{inv.title}</span>
                        <span className="text-xs text-gray-400 ml-2">· {client?.companyName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-red-600 font-medium">Tähtaja ületanud</span>
                        <span className="text-sm font-bold text-red-600">{formatCurrency(Number(inv.amount))}</span>
                        <ArrowUpRight size={13} className="text-red-400" />
                      </div>
                    </button>
                  );
                })}
                {invoices.filter((i) => i.status === 'ready_to_send').map((inv) => {
                  const project = projects.find((p) => p.id === inv.projectId);
                  const client = clients.find((c) => c.id === project?.clientId);
                  return (
                    <button
                      key={inv.id}
                      onClick={() => navigate(`/projects/${inv.projectId}/invoices`)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-white border border-yellow-100 rounded-lg hover:bg-yellow-50 text-left transition-colors"
                    >
                      <div>
                        <span className="text-xs font-medium text-gray-800">{inv.title}</span>
                        <span className="text-xs text-gray-400 ml-2">· {client?.companyName}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-yellow-700 font-medium">Saatmiseks valmis</span>
                        <span className="text-sm font-bold text-gray-800">{formatCurrency(Number(inv.amount))}</span>
                        <ArrowUpRight size={13} className="text-yellow-500" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════════════════════════════ TAB: PROFITABILITY ═════════════════ */}
      {tab === 'profit' && (
        <ProfitabilityTable
          projects={projects}
          clientName={(id) => clients.find((c) => c.id === id)?.companyName ?? ''}
          onOpen={(id) => navigate(`/projects/${id}`)}
        />
      )}

      {/* ═══════════════════════════════ TAB: INVOICES ══════════════════════ */}
      {tab === 'invoices' && (
        <div className="space-y-6">
          {/* Income pipeline calendar */}
          <div className="bg-white rounded-xl border border-gray-100">
            <div className="px-5 py-4 border-b border-gray-50 space-y-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <h2 className="font-semibold text-gray-900 text-sm">Sissetulekute kalender</h2>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {pipelineView === 'list' ? 'Tulevased ja ootel arved kuude kaupa' : 'Arved tähtaja järgi kalendris'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {pipelineView === 'list' && (
                    <button
                      onClick={() => setSortOrder((s) => s === 'asc' ? 'desc' : 'asc')}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                    >
                      <ArrowDownUp size={12} />
                      {sortOrder === 'asc' ? 'Vanemad ees' : 'Uuemad ees'}
                    </button>
                  )}
                  {/* List / Calendar toggle */}
                  <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setPipelineView('list')}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md font-medium transition-colors ${
                        pipelineView === 'list' ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <List size={12} /> Nimekiri
                    </button>
                    <button
                      onClick={() => setPipelineView('calendar')}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-md font-medium transition-colors ${
                        pipelineView === 'calendar' ? 'bg-white text-brand-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <CalendarDays size={12} /> Kalender
                    </button>
                  </div>
                </div>
              </div>
              {pipelineView === 'list' && (
              <div className="flex gap-2 flex-wrap items-center">
                {/* Quick period buttons */}
                {[
                  { label: 'This month', fn: () => { const s = startOfMonth(today); const e = endOfMonth(today); setDateFrom(format(s, 'yyyy-MM-dd')); setDateTo(format(e, 'yyyy-MM-dd')); } },
                  { label: 'This Q', fn: () => { const s = startOfQuarter(today); const e = endOfQuarter(today); setDateFrom(format(s, 'yyyy-MM-dd')); setDateTo(format(e, 'yyyy-MM-dd')); } },
                  { label: 'Next Q', fn: () => { const s = startOfQuarter(addQuarters(today, 1)); const e = endOfQuarter(addQuarters(today, 1)); setDateFrom(format(s, 'yyyy-MM-dd')); setDateTo(format(e, 'yyyy-MM-dd')); } },
                  { label: format(today, 'yyyy'), fn: () => { const s = startOfYear(today); const e = endOfYear(today); setDateFrom(format(s, 'yyyy-MM-dd')); setDateTo(format(e, 'yyyy-MM-dd')); } },
                ].map(({ label, fn }) => (
                  <button
                    key={label}
                    onClick={fn}
                    className="text-xs px-2 py-1 border border-gray-200 rounded-lg hover:bg-brand-50 hover:border-brand-300 hover:text-brand-600 text-gray-500 transition-colors"
                  >
                    {label}
                  </button>
                ))}
                <div className="flex items-center gap-1.5">
                  <Calendar size={12} className="text-gray-400" />
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                  <span className="text-xs text-gray-400">—</span>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 text-gray-600"
                >
                  <option value="all">Kõik staatused</option>
                  <option value="not_ready">Not Ready</option>
                  <option value="ready_to_send">Ready to Send</option>
                  <option value="sent">Sent</option>
                  <option value="overdue">Overdue</option>
                </select>
                {(dateFrom || dateTo || statusFilter !== 'all') && (
                  <button
                    onClick={() => { setDateFrom(''); setDateTo(''); setStatusFilter('all'); }}
                    className="text-xs text-brand-500 hover:text-brand-700 font-medium"
                  >
                    Tühista filtrid
                  </button>
                )}
              </div>
              )}
            </div>

            {pipelineView === 'calendar' ? (
              <>
                {/* Status legend */}
                <div className="flex gap-4 flex-wrap px-5 py-2.5 border-b border-gray-50">
                  {[
                    { color: '#22c55e', label: 'Laekunud' },
                    { color: '#3b82f6', label: 'Saadetud' },
                    { color: '#eab308', label: 'Saatmiseks valmis' },
                    { color: '#ef4444', label: 'Tähtaja ületanud' },
                    { color: '#d1d5db', label: 'Planeeritud' },
                  ].map(({ color, label }) => (
                    <div key={label} className="flex items-center gap-1.5 text-xs text-gray-500">
                      <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: color }} />
                      {label}
                    </div>
                  ))}
                </div>
                <InvoiceCalendar
                  invoices={calendarInvoices}
                  clientName={clientNameForProject}
                  onSelect={(projectId) => navigate(`/projects/${projectId}/invoices`)}
                  onAddForDate={(dateStr) => setQuickAddDate(dateStr)}
                />
              </>
            ) : (
            <div className="divide-y divide-gray-50 max-h-[360px] overflow-y-auto scrollbar-thin">
              {sortedMonthGroups.length === 0 && (
                <div className="px-5 py-8 text-center text-gray-300 text-sm">Tulevasi arveid pole</div>
              )}
              {sortedMonthGroups.map((group) => {
                const isPast = isBefore(group.monthDate, today);
                return (
                  <div key={group.monthLabel}>
                    <div className={`px-5 py-2.5 flex items-center justify-between sticky top-0 ${isPast ? 'bg-gray-50' : 'bg-white'}`}>
                      <span className={`text-xs font-semibold uppercase tracking-wider ${
                        isSameMonth(group.monthDate, today) ? 'text-brand-600' : isPast ? 'text-gray-400' : 'text-gray-600'
                      }`}>
                        {group.monthLabel}
                        {isSameMonth(group.monthDate, today) && <span className="ml-1.5 text-brand-400 normal-case font-medium">· praegune kuu</span>}
                      </span>
                      <span className={`text-sm font-bold ${isPast ? 'text-gray-400' : 'text-gray-900'}`}>
                        {formatCurrency(group.total)}
                      </span>
                    </div>
                    {group.items.map((inv) => {
                      const project = projects.find((p) => p.id === inv.projectId);
                      const client = clients.find((c) => c.id === project?.clientId);
                      return (
                        <div
                          key={inv.id}
                          onClick={() => navigate(`/projects/${inv.projectId}/invoices`)}
                          className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left cursor-pointer"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-800 truncate">{inv.title}</p>
                            <p className="text-xs text-gray-400 truncate">
                              {client?.companyName}
                              {inv.plannedSendDate && (
                                <span className="text-gray-300"> · plaanitud {formatDate(inv.plannedSendDate)}</span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-3" onClick={(e) => e.stopPropagation()}>
                            <label className="flex items-center gap-1.5 text-[10px] text-gray-400 cursor-pointer" title="Arve saatmise kuupäev — vali kuupäev, et märkida arve saadetuks">
                              Saadetud
                              <input
                                type="date"
                                value={inv.sentDate ? inv.sentDate.split('T')[0] : ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const updates: Partial<InvoiceMilestone> = { sentDate: v };
                                  // Recording a send on a not-yet-sent invoice also flips its status.
                                  if (v && (inv.status === 'not_ready' || inv.status === 'ready_to_send')) {
                                    updates.status = 'sent';
                                  }
                                  updateInvoice(inv.id, updates);
                                }}
                                className={`text-xs border rounded-lg px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400 ${
                                  inv.sentDate ? 'border-gray-200 text-gray-600' : 'border-dashed border-gray-300 text-gray-400'
                                }`}
                              />
                            </label>
                            <InvoiceBadge status={inv.status} />
                            <span className="text-sm font-semibold text-gray-800">{formatCurrency(Number(inv.amount))}</span>
                            <ChevronRight size={13} className="text-gray-300" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
            )}
          </div>

          {/* Invoice detail table */}
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-50">
              <h2 className="font-semibold text-gray-900 text-sm">Kõik arved</h2>
              <p className="text-xs text-gray-400 mt-0.5">{invoiceRows.length} arvet</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {[
                      { col: 'title', label: 'Arve' },
                      { col: 'project', label: 'Projekt' },
                      { col: 'client', label: 'Klient' },
                      { col: 'salesperson', label: 'Müügijuht' },
                      { col: 'amount', label: 'Summa' },
                      { col: 'status', label: 'Staatus' },
                      { col: 'plannedSendDate', label: 'Tähtaeg' },
                      { col: 'paidDate', label: 'Makstud' },
                    ].map(({ col, label }) => (
                      <th
                        key={col}
                        onClick={() => col !== 'title' && col !== 'salesperson' && col !== 'paidDate' && toggleSort(col)}
                        className={`px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap ${
                          col !== 'title' && col !== 'salesperson' && col !== 'paidDate'
                            ? 'cursor-pointer hover:text-gray-600 select-none'
                            : ''
                        }`}
                      >
                        {label}
                        {col !== 'title' && col !== 'salesperson' && col !== 'paidDate' && (
                          <SortIcon col={col} />
                        )}
                      </th>
                    ))}
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {invoiceRows.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-8 text-center text-gray-300 text-sm">Arveid pole</td>
                    </tr>
                  )}
                  {invoiceRows.map(({ inv, project, client, salesOwner }) => (
                    <tr
                      key={inv.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => project && navigate(`/projects/${project.id}/invoices`)}
                    >
                      <td className="px-4 py-3">
                        <span className="font-medium text-gray-900 text-xs">{inv.title}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-[160px] truncate">{project?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{client?.companyName ?? '—'}</td>
                      <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{salesOwner?.name ?? '—'}</td>
                      <td className="px-4 py-3 text-xs font-semibold text-gray-900 whitespace-nowrap">
                        {formatCurrency(Number(inv.amount))}
                      </td>
                      <td className="px-4 py-3">
                        <StatusSelect inv={inv} onChange={(updates) => updateInvoice(inv.id, updates)} />
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {invoiceTimelineDate(inv) ? formatDate(invoiceTimelineDate(inv) as string) : '—'}
                        {inv.sentDate && inv.plannedSendDate && inv.sentDate.split('T')[0] !== inv.plannedSendDate.split('T')[0] && (
                          <span className="block text-[10px] text-gray-300" title={`Plaanitud ${formatDate(inv.plannedSendDate)}, saadetud ${formatDate(inv.sentDate)}`}>
                            saadetud · plaanitud {formatDate(inv.plannedSendDate)}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                        {inv.paidDate ? formatDate(inv.paidDate) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        <ChevronRight size={13} className="text-gray-300" />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {invoiceRows.length > 0 && (
                  <tfoot>
                    <tr className="border-t border-gray-200 bg-gray-50">
                      <td colSpan={4} className="px-4 py-3 text-xs font-semibold text-gray-600">Kokku</td>
                      <td className="px-4 py-3 text-xs font-bold text-gray-900">
                        {formatCurrency(sum(invoiceRows.map((r) => r.inv.amount)))}
                      </td>
                      <td colSpan={4} />
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </div>
      )}

      {quickPayTarget && (
        <QuickPayModal
          open={!!quickPayTarget}
          projectName={quickPayTarget.projectName}
          projectValue={quickPayTarget.projectValue}
          paidSoFar={quickPayTarget.paidSoFar}
          projectId={quickPayTarget.projectId}
          onSave={(inv) => { addInvoice(inv); setQuickPayTarget(null); }}
          onClose={() => setQuickPayTarget(null)}
        />
      )}

      <QuickAddInvoiceModal
        open={quickAddDate !== null}
        date={quickAddDate ?? ''}
        projects={projectOptions}
        onClose={() => setQuickAddDate(null)}
        onSave={async (inv) => { await addInvoice(inv); setQuickAddDate(null); }}
      />
    </div>
  );
}
